import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Shared";
import { toast } from "sonner";
import { Send, Bot, ScrollText, ListChecks, Trash2, Sparkles } from "lucide-react";

// Render **bold** segments; everything else plain. Keeps the agent's replies readable.
function RichText({ text }) {
  const parts = String(text || "").split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith("**") && p.endsWith("**") ? (
          <strong key={i} className="text-white">{p.slice(2, -2)}</strong>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </>
  );
}

function ActionChips({ actions }) {
  if (!actions?.length) return null;
  const label = (a) => {
    if (a.tool === "add_constraint") return a.result?.ok ? `Constraint saved` : `Constraint not saved`;
    if (a.tool === "regenerate_schedule") return `Regenerated · ${a.result?.changed_slots ?? 0} slots changed`;
    if (a.tool === "query_schedule") return `Read schedule`;
    if (a.tool === "query_constraints") return `Read constraints`;
    return a.tool;
  };
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {actions.map((a, i) => (
        <span key={i} data-testid={`action-chip-${a.tool}`}
          className="text-[10px] font-mono-rs uppercase tracking-wider px-2 py-0.5 border border-[#34E5B0]/40 text-[#34E5B0] bg-[#34E5B0]/10">
          {label(a)}
        </span>
      ))}
    </div>
  );
}

function Chat() {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const scrollDown = () => setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 60);

  useEffect(() => {
    api.get("/agent/history").then((r) => { setMessages(r.data); scrollDown(); }).catch(() => {});
  }, []);

  const send = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setText("");
    setMessages((m) => [...m, { role: "user", content: t }]);
    scrollDown();
    setBusy(true);
    try {
      const r = await api.post("/agent/chat", { message: t });
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply, actions: r.data.actions }]);
      scrollDown();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — something went wrong handling that. Please try again." }]);
    } finally { setBusy(false); }
  };

  const prompts = [
    "Tech Smith can't work nights starting Oct 1",
    "Who is working nights this week?",
    "Is anyone unassigned on Friday?",
  ];

  return (
    <div className="bg-[#0F1219] border border-slate-800 flex flex-col h-[68vh]">
      <div data-testid="agent-feed" className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center pt-10">
            <Sparkles className="w-8 h-8 text-[#D902EE] mx-auto mb-3" />
            <p className="text-slate-400 text-sm max-w-md mx-auto">
              Describe an availability change in plain English and I'll turn it into a scheduling rule —
              or ask me about the current schedule. I confirm before saving anything.
            </p>
            <div className="flex flex-wrap gap-2 justify-center mt-4">
              {prompts.map((p) => (
                <button key={p} data-testid="agent-suggestion" onClick={() => setText(p)}
                  className="text-xs px-3 py-1.5 border border-slate-700 text-slate-300 hover:border-[#00F0FF] hover:text-white transition-all">
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "assistant" && <Bot className="w-5 h-5 text-[#00F0FF] shrink-0 mt-1" />}
            <div data-testid={`agent-msg-${m.role}`} className={`max-w-[78%] px-3.5 py-2.5 border text-sm whitespace-pre-wrap leading-relaxed ${
              m.role === "user" ? "bg-[#8A2BE2]/15 border-[#B15CFF]/40 text-white" : "bg-[#05060A] border-slate-800 text-slate-200"}`}>
              <RichText text={m.content} />
              {m.role === "assistant" && <ActionChips actions={m.actions} />}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex gap-2 items-center text-[#00F0FF]">
            <Bot className="w-5 h-5" />
            <span className="text-sm font-mono-rs tracking-wider animate-pulse">THINKING…</span>
          </div>
        )}
        <div ref={endRef} />
      </div>
      <div className="border-t border-slate-800 p-3 flex gap-2">
        <input data-testid="agent-input" value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()} placeholder="e.g. Maria can only work Tue & Thu 8–1, starting next Monday…"
          className="flex-1 bg-[#05060A] border border-slate-700 px-3 py-2 text-sm text-white outline-none focus:border-[#00F0FF] transition-colors" />
        <button data-testid="agent-send" onClick={send} disabled={busy}
          className="px-4 border border-[#D902EE] bg-[#D902EE]/10 text-white hover:bg-[#D902EE]/25 transition-all disabled:opacity-40">
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function Rules() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/constraints").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    if (!window.confirm("Remove this constraint? The schedule will keep it until you regenerate.")) return;
    try { await api.delete(`/constraints/${id}`); toast.success("Constraint removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-2" data-testid="rules-list">
      {items.length === 0 && <p className="text-slate-500 text-sm p-4">No constraints yet. Add one from the Assistant tab.</p>}
      {items.map((c) => (
        <div key={c.id} className={`flex items-center justify-between gap-3 px-4 py-3 border ${c.active ? "border-slate-800 bg-[#0F1219]" : "border-slate-800/50 bg-[#0F1219]/40 opacity-60"}`}>
          <div className="min-w-0">
            <div className="text-sm text-white">{c.summary}</div>
            <div className="text-[10px] font-mono-rs uppercase tracking-wider text-slate-500 mt-0.5">
              by {c.created_by_name} {!c.active && "· removed"}{c.original_text ? ` · "${c.original_text}"` : ""}
            </div>
          </div>
          {c.active && (
            <button data-testid={`rule-remove-${c.id}`} onClick={() => remove(c.id)}
              className="text-slate-500 hover:text-[#FF003C] transition-colors shrink-0"><Trash2 className="w-4 h-4" /></button>
          )}
        </div>
      ))}
    </div>
  );
}

function Audit() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/agent/audit").then((r) => setItems(r.data)).catch(() => {}); }, []);
  const desc = (e) => {
    if (e.event === "constraint_added") return `Added constraint — ${e.detail?.summary}`;
    if (e.event === "constraint_removed") return `Removed constraint — ${e.detail?.summary}`;
    if (e.event === "schedule_regenerated") return `Regenerated schedule — ${e.detail?.changed_slots} slots changed${e.detail?.unassigned ? `, ${e.detail.unassigned} unassigned` : ""}`;
    return e.event;
  };
  return (
    <div className="space-y-2" data-testid="audit-list">
      {items.length === 0 && <p className="text-slate-500 text-sm p-4">No activity logged yet.</p>}
      {items.map((e) => (
        <div key={e.id} className="px-4 py-3 border border-slate-800 bg-[#0F1219]">
          <div className="text-sm text-slate-200">{desc(e)}</div>
          <div className="text-[10px] font-mono-rs uppercase tracking-wider text-slate-500 mt-0.5">
            {e.actor_name} · {new Date(e.created_at).toLocaleString()}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AssistantAgent() {
  const [tab, setTab] = useState("chat");
  const tabCls = (t) => `flex items-center gap-2 px-4 py-2 text-sm font-mono-rs uppercase tracking-wider border-b-2 transition-all duration-300 ${
    tab === t ? "border-[#D902EE] text-white" : "border-transparent text-slate-400 hover:text-white"}`;
  return (
    <div>
      <PageHeader title="Assistant" subtitle="// Scheduling constraint agent" />
      <div className="flex border-b border-slate-800 mb-6">
        <button data-testid="agent-tab-chat" onClick={() => setTab("chat")} className={tabCls("chat")}><Bot className="w-4 h-4" /> Assistant</button>
        <button data-testid="agent-tab-rules" onClick={() => setTab("rules")} className={tabCls("rules")}><ListChecks className="w-4 h-4" /> Rules</button>
        <button data-testid="agent-tab-audit" onClick={() => setTab("audit")} className={tabCls("audit")}><ScrollText className="w-4 h-4" /> Audit</button>
      </div>
      {tab === "chat" && <Chat />}
      {tab === "rules" && <Rules />}
      {tab === "audit" && <Audit />}
    </div>
  );
}
